import { getBreakpointValue } from '@/modules/utils';

// Tailwind's min-width screens, read from tailwind.config.js through the
// preval'd resolveConfig in modules/utils, so the numbers never diverge from
// the `@screen` blocks in the stylesheets. (`xs` is a max-width range and
// `tall` a raw media query; neither is a width tier.)
const NAMES = ['sm', 'md', 'lg', 'xl'];

const currentBreakpoint = () => {
  const width = window.innerWidth;
  let current = null;
  NAMES.forEach(name => {
    if (width >= getBreakpointValue(name)) current = name;
  });
  return current;
};

/**
 * Reactive Tailwind breakpoint for components that decide a size inline and
 * must agree with the stylesheet about which layout they are in.
 *
 * - `breakpoint`: the largest matching screen name (`null` below `sm`).
 * - `isLgViewport`: true at or above `lg`; add sibling computeds the same way
 *   when another tier is needed.
 *
 * modules/utils has getCurrentBreakpoint() and mixins/screen-width.js has
 * lg(), but both read the width once; this one listens through matchMedia so
 * Split View and rotation re-evaluate it.
 */
const breakpoint = {
  data() {
    return {
      breakpoint: typeof window !== 'undefined' ? currentBreakpoint() : null,
    };
  },
  mounted() {
    this.onBreakpointChange = () => {
      this.breakpoint = currentBreakpoint();
    };
    this.breakpointQueries = NAMES.map(name =>
      window.matchMedia(`(min-width: ${getBreakpointValue(name)}px)`)
    );
    // the app still targets iOS 13, whose WKWebView only has addListener
    this.breakpointQueries.forEach(mql => {
      if (typeof mql.addEventListener === 'function') {
        mql.addEventListener('change', this.onBreakpointChange);
      } else {
        mql.addListener(this.onBreakpointChange);
      }
    });
  },
  beforeDestroy() {
    (this.breakpointQueries || []).forEach(mql => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', this.onBreakpointChange);
      } else {
        mql.removeListener(this.onBreakpointChange);
      }
    });
  },
  computed: {
    isLgViewport() {
      return !!this.breakpoint && getBreakpointValue(this.breakpoint) >= getBreakpointValue('lg');
    },
  },
};

export default breakpoint;
